/**
 * App settings supporting sections.
 * Renders the summary and danger-zone content blocks.
 */

import type { AppRecord, AppStats } from "@/services/app-service"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { BookOpen, Bot, ShieldCheck, Trash2, Workflow } from "lucide-react"

function formatUpdatedAt(updatedAt?: string | null): string {
  if (!updatedAt) return "Never"

  return new Date(updatedAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

interface AppSummarySectionProps {
  app: AppRecord
  stats: AppStats | null
  isStatsLoading: boolean
}

interface SummaryStatProps {
  label: string
  value: number | string
  icon?: typeof Bot
}

function SummaryStat({ label, value, icon: Icon }: SummaryStatProps) {
  return (
    <div className="rounded-lg border bg-muted/20 p-4">
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        <span className="text-sm">{label}</span>
      </div>
      <p className="text-2xl font-semibold">{value}</p>
    </div>
  )
}

export function AppSummarySection({ app, stats, isStatsLoading }: AppSummarySectionProps) {
  return (
    <section className="space-y-4">
      <div className="space-y-1.5">
        <h3 className="text-sm font-medium">App Summary</h3>
        <p className="text-sm text-muted-foreground">
          Quick read-only reference for the resources in this app.
        </p>
      </div>

      <div className="rounded-lg border bg-card p-6">
        {isStatsLoading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-20 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <SummaryStat label="Agents" value={stats?.agentCount ?? 0} icon={Bot} />
            <SummaryStat label="Workflows" value={stats?.workflowCount ?? 0} icon={Workflow} />
            <SummaryStat label="Knowledge Bases" value={stats?.knowledgeCount ?? 0} icon={BookOpen} />
            <div className="rounded-lg border bg-muted/20 p-4">
              <Label className="text-sm text-muted-foreground">Last Updated</Label>
              <p className="mt-2 text-sm font-medium">{formatUpdatedAt(app.updatedAt || app.createdAt)}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

interface AppDangerZoneSectionProps {
  isDefaultApp: boolean
  onUninstallClick: () => void
}

export function AppDangerZoneSection({
  isDefaultApp,
  onUninstallClick,
}: AppDangerZoneSectionProps) {
  return (
    <section className="space-y-4">
      <div className="space-y-1.5">
        <h3 className="text-sm font-medium text-destructive">Danger Zone</h3>
        <p className="text-sm text-muted-foreground">Manage destructive actions for this app.</p>
      </div>

      <div className="rounded-lg border border-destructive/30 bg-card p-6">
        {isDefaultApp ? (
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="mb-2 flex items-center gap-2 font-medium">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Default app protected
            </div>
            <p className="text-sm text-muted-foreground">
              This is the workspace default app and cannot be uninstalled.
            </p>
            <Button className="mt-4" variant="outline" disabled>
              Default App Protected
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="font-medium">Uninstall App</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Remove this app from the workspace. This may affect linked agents, workflows,
                  knowledge bases, and chats.
                </p>
              </div>
              <Button variant="destructive" onClick={onUninstallClick}>
                <Trash2 className="mr-2 h-4 w-4" />
                Uninstall App
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

AppSummarySection.displayName = "AppSummarySection"
AppDangerZoneSection.displayName = "AppDangerZoneSection"
